import React, {useState} from 'react';
import {
  Box,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {buildingServiceCreate, buildingServiceUpdate} from 'src/apis';
import {Building} from 'src/apis/autoGenerate/types';
import message from 'src/components/Message';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';

interface AddBuildingModalProps {
  open: boolean;
  handleConfirm: VoidFunction;
  handleCancel: VoidFunction;
  apartmentId: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, the dialog updates this building instead of creating one. */
  building?: Building;
}

export default function AddBuildingModal({
  open,
  handleConfirm,
  handleCancel,
  apartmentId,
  setOpen,
  building,
}: AddBuildingModalProps) {
  const isEdit = Boolean(building?.id);
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(selectAccount);

  // Basic building info
  const [name, setName] = useState(building?.name || '');
  const [description, setDescription] = useState(building?.description || '');
  const [floorNum, setFloorNum] = useState<number | ''>(building?.floorNum ?? '');
  const [floorUnitCount, setFloorUnitCount] = useState<number | ''>(
    building?.floorUnitCount ?? ''
  );

  const handleAddBuilding = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      message.error('Please enter a building name');
      return;
    }

    // Remove validation for floorNum and floorUnitCount since they're now optional

    // Check if organization ID is available
    const orgId = account?.userDetail?.organizations?.[0]?.id;
    if (!orgId) {
      message.error('Organization information is missing');
      return;
    }

    setLoading(true);

    try {
      // Create or update the building - only include floor fields if they have values
      const buildingData: Building = {
        apartmentId,
        name: trimmedName,
        description,
        orgId,
      };

      // Only add floor fields if they have values
      if (floorNum && floorNum > 0) {
        buildingData.floorNum = Number(floorNum);
      }

      if (floorUnitCount && floorUnitCount > 0) {
        buildingData.floorUnitCount = Number(floorUnitCount);
      }

      if (isEdit && building?.id) {
        await buildingServiceUpdate(building.id, {
          buildingId: building.id,
          building: buildingData,
        });
      } else {
        await buildingServiceCreate({
          building: buildingData,
        });
      }

      // Reset the form
      setName('');
      setDescription('');
      setFloorNum('');
      setFloorUnitCount('');

      handleConfirm();
      message.success(isEdit ? 'Building updated successfully' : 'Building created successfully');
    } catch (error) {
      console.error(isEdit ? 'Failed to update building:' : 'Failed to create building:', error);
      message.error(isEdit ? 'Failed to update building' : 'Failed to create building');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="add-building-dialog-title"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 450,
          maxWidth: 450,
        },
      }}
    >
      <DialogTitle sx={{m: 0, p: 2}} id="add-building-dialog-title">
        {isEdit ? 'Edit Building' : 'Add a New Building'}
      </DialogTitle>
      <DialogContent sx={{padding: 3, overflow: 'visible'}}>
        <Box component="form">
          {/* Building Name */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Building Name <span style={{color: 'inherit'}}>*</span>
          </Typography>
          <TextField
            fullWidth
            required
            size="small"
            id="buildingName"
            placeholder="Building Name"
            variant="outlined"
            value={name}
            onChange={e => setName(e.target.value)}
            sx={{mb: 3}}
          />

          {/* Floor Number */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Number of Floors
          </Typography>
          <TextField
            fullWidth
            size="small"
            id="floorNum"
            placeholder="Number of Floors"
            variant="outlined"
            type="number"
            inputProps={{min: 1}}
            value={floorNum}
            onChange={e => setFloorNum(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{mb: 3}}
          />

          {/* Units Per Floor */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Units Per Floor
          </Typography>
          <TextField
            fullWidth
            size="small"
            id="floorUnitCount"
            placeholder="Units Per Floor"
            variant="outlined"
            type="number"
            inputProps={{min: 1}}
            value={floorUnitCount}
            onChange={e => setFloorUnitCount(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{mb: 3}}
          />

          {/* Description */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Description
          </Typography>
          <TextField
            fullWidth
            size="small"
            id="description"
            placeholder="Description"
            variant="outlined"
            multiline
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
            sx={{mb: 1}}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          sx={{width: loading ? 110 : 'auto'}}
          variant="contained"
          loading={loading}
          loadingPosition="start"
          startIcon={<></>}
          onClick={handleAddBuilding}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
